import { CalendarToday, Email, ExpandMore, Person } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'

import type { EmailItem } from '@/shared/types/email.types.ts'
import { formatEmailStatus, getEmailStatusConfig } from '@/shared/utils/email-status.utils.tsx'

interface Props {
  emails?: EmailItem[]
  isLoading?: boolean
}

export const SectionEmailHistory = ({ emails, isLoading }: Props) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Email color="primary" />
            <Typography variant="h6" color="primary">
              Email History
            </Typography>
            {emails && (
              <Chip
                label={`${emails.length} email${emails.length !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {(() => {
            if (isLoading) {
              return (
                <Box display="flex" alignItems="center" gap={1} py={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading emails...
                  </Typography>
                </Box>
              )
            }

            if (!emails || emails.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" py={2}>
                  No emails found for this appointment.
                </Typography>
              )
            }

            return (
              <Stack spacing={2}>
                {emails.map((email: EmailItem, index: number) => {
                  const statusConfig = getEmailStatusConfig(email.status || 'unknown')
                  const emailDate = email.date
                    ? format(new Date(email.date), 'MMM dd, yyyy HH:mm')
                    : 'Unknown date'

                  return (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        flexWrap="wrap"
                        gap={2}
                      >
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {email.emailName || 'Unnamed Email'}
                            </Typography>
                            <Chip
                              icon={statusConfig.icon}
                              label={formatEmailStatus(email.status || 'unknown')}
                              size="small"
                              color={statusConfig.color}
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            To: {email.emailAddress || 'Unknown recipient'}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarToday sx={{ fontSize: 16 }} color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {emailDate}
                            </Typography>
                          </Box>
                          {email.scoutName && (
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                              <Person sx={{ fontSize: 16 }} color="action" />
                              <Typography variant="caption" color="text.secondary">
                                Scout: {email.scoutName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  )
                })}
              </Stack>
            )
          })()}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default SectionEmailHistory
